<template>
  <div>
    <v-row class="user-row">
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="First name"
          class="contact-label"
        />
        <sn-text-field
          v-model="firstName"
          placeholder="Enter first name"
          required
          maxlength="50"
          :rules="[limit50Chars, notEmpty]"
        />
      </v-flex>
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="Last name"
          class="contact-label"
        />
        <sn-text-field
          v-model="lastName"
          placeholder="Enter last name"
          required
          maxlength="50"
          :rules="[limit50Chars, notEmpty]"
        />
      </v-flex>
    </v-row>
    <v-row class="user-row">
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="Email"
          class="contact-label"
        />
        <sn-text-field
          v-model="email"
          :error-messages="emailErrors"
          placeholder="Enter email"
          required
          :disabled="edit"
          :rules="[validateEmail, notEmpty]"
          @blur="getExistingUser"
        />
      </v-flex>
      <v-flex
        class="md6 sm12 xs12 user-column"
      >
        <sn-label
          title="Account type"
          class="contact-label"
        />
        <sn-combobox
          v-model="roleType"
          :items="roleTypes"
          placeholder="Select a role for this user"
          attach
          @search-input="handleSearchInput($event, 'role')"
        />
      </v-flex>
    </v-row>
    <v-row class="user-row">
      <v-flex
        class="md6 sm12 xs12 user-column"
      >
        <sn-label
          title="Time zone"
          class="contact-label"
        />
        <sn-combobox
          v-model="timeZone"
          :items="timeZoneOptions"
          placeholder="Select a preferred time zone for this user"
          attach
          @search-input="handleSearchInput($event, 'timezone')"
        />
      </v-flex>
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="Environments"
          class="contact-label"
        />
        <env-select
          :value="selectedEnvironments"
          :items="environmentOptions"
          :disabled="true"
          :placeholder="'Inherited from account type'"
        />
      </v-flex>
    </v-row>
    <v-row
      v-if="!isSnAdminCompany"
      class="user-row"
    >
      <v-flex class="md12">
        <sn-label
          title="Org IDs"
          class="contact-label"
        />
        <mers-org-combobox
          v-model="selectedOrgs"
          class="orgs"
          :mers-orgs="orgs"
          :disabled="orgIdsDisabled"
          assign-dropdown-ref="userInfoOrgsCombobox"
          @click="handleClick"
        />
      </v-flex>
    </v-row>
    <v-row
      v-if="edit"
      class="user-row"
    >
      <v-flex v-if="lastLoginAt == null" class="md12 mb-2">
        <sn-btn
          data-cy="resend-invite"
          text="Resend invite"
          type="text"
          size="small"
          :disabled="loading"
          @click="resendInvite"
        />
      </v-flex>
      <v-flex class="md12">
        <sn-btn
          data-cy="password-reset"
          text="Require password reset"
          type="text"
          size="small"
          :disabled="passwordResetLoading"
          @click="showPasswordResetModal = true"
        />
      </v-flex>
    </v-row>
    <v-row
      v-if="edit"
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <div class="d-flex">
          <sn-btn
            style="marginTop: 10px"
            text="Save"
            type="primary"
            size="large"
            data-cy="personal-info-save-btn"
            :disabled="saveBtnDisabled"
            :loading="saveBtnLoading"
            @click="handleSave"
          />
        </div>
      </v-col>
    </v-row>
    <sn-modal
      v-model="showPasswordResetModal"
      type="decision"
      :title="`Require password reset?`"
      data-cy="confirmation-modal"
      primary-text="Yes"
      secondary-text="Cancel"
      :loading="passwordResetLoading"
      @submit="forcePasswordReset"
      @close="showPasswordResetModal=false"
    >
      <span>This action will require the user to reset their password.</span>
    </sn-modal>
  </div>
</template>

<script>

import mersOrgCombobox from './mersOrgCombobox.vue'
import envSelect from './envSelect.vue'

import snEvaultService, { TIMEZONES } from '../../services/snEvaultService'
import { rolesPermissions } from '../graphql/companies_queries.gql'
import { user, editUserMutation } from '../graphql/users/users_queries.gql'
import { company, inviteUser, updateUsersForcePasswordReset } from '../graphql/users/user_access.gql'
import { mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'

export default {
  name: 'UserInfo',
  components: {
    envSelect,
    mersOrgCombobox
  },
  props: {
    edit: {
      type: Boolean,
      required: true,
      default: false
    },
    user: {
      type: Object,
      required: false,
      default: () => {}
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    },
    userGuid: {
      type: String,
      default: '',
      required: false
    }
  },
  data () {
    return {
      dbFirstName: this.user?.firstName || '',
      firstName: '',
      dbLastName: this.user?.lastName || '',
      lastName: '',
      dbTimeZone: this.user?.timezone || '',
      timeZone: '',
      defaultTimeZone: '',
      dbEmail: this.user?.email || '',
      email: '',
      emailErrors: [],
      emailAlreadyInUse: false,
      dbRoleType: this.user?.roleType || '',
      roleType: '',
      roleTypes: [],
      orgs: [],
      dbOrgs: [],
      selectedOrgs: [],
      companyMersOrgCount: 0,
      timeZoneOptions: TIMEZONES.map(tz => tz.label),
      environmentOptions: ['Staging', 'Production'],
      validTimeZone: true,
      validRoleType: true,
      roleEnvironments: {},
      loading: false,
      saveBtnLoading: false,
      initialLoad: true,
      passwordResetLoading: false,
      showPasswordResetModal: false,
      companyName: ''
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'user',
      mersOrgId: 'mersOrgId'
    }),
    selectedEnvironments () {
      if (this.roleType) {
        return this.roleEnvironments[this.roleType] || []
      }
      return []
    },
    limit50Chars () {
      return (input) => input && input?.length >= 50 ? '50 character limit' : true
    },
    notEmpty () {
      return (input) => input && input?.length > 0 ? true : 'Required field'
    },
    validateEmail () {
      return (input) => input ? snEvaultService.validateEmail(input) ? true : 'Invalid email format' : false
    },
    dbValues () {
      return { firstName: this.dbFirstName, lastName: this.dbLastName, timeZone: this.dbTimeZone, email: this.dbEmail, roleType: this.dbRoleType, selectedOrgGuids: this.dbOrgs?.map(org => org.guid) }
    },
    currentValues () {
      const { firstName, lastName, timeZone, email, roleType, selectedOrgGuids } = this
      return { firstName, lastName, timeZone, email, roleType, selectedOrgGuids }
    },
    selectedOrgGuids () {
      return this.selectedOrgs.map(org => org.guid)
    },
    managesUsers () {
      return snEvaultService.hasPermission(this.loggedInUser, 'manageUsers')
    },
    isEditingSelf () {
      if (this.edit) {
        return this.loggedInUser.guid === this.userGuid
      }
      return false
    },
    orgIdsDisabled () {
      if (this.edit) {
        return !this.managesUsers || this.isEditingSelf
      }
      return false
    },
    hasChanges () {
      if (this.edit) {
        return !_.isEqual(this.dbValues, this.currentValues)
      }
      return true
    },
    dataEntryComplete () {
      return this.selectedEnvironments?.length > 0 &&
        (this.isSnAdminCompany || this.selectedOrgs?.length > 0) &&
        this.validateEmail(this.email) === true &&
        this.limit50Chars(this.firstName) === true &&
        this.limit50Chars(this.lastName) === true &&
        [this.firstName, this.lastName, this.email, this.timeZone, this.roleType].every(val => Boolean(val)) &&
        !this.emailAlreadyInUse &&
        this.validTimeZone &&
        this.validRoleType
    },
    saveBtnDisabled () {
      return !this.hasChanges || !this.dataEntryComplete
    },
    enabledEnvironments () {
      return this.roleEnvironments[this.roleType]
    },
    lastLoginAt () { return this.user?.lastLoginAt },
    isSnAdminCompany () { return this.user?.company?.name === 'SnAdmin Company' || this.companyName === 'SnAdmin Company' }
  },
  watch: {
    dataEntryComplete (val) {
      this.$emit('validation', val)
    },
    currentValues: {
      handler () {
        if (this.initialLoad) {
          this.initialLoad = false
          return
        }
        if (this.hasChanges) {
          this.$emit('change', this.currentValues)
        } else {
          this.$emit('change', {})
        }
      },
      deep: true
    },
    hasChanges (val) {
      this.$emit('changed', val)
    },
    user () {
      if (this.user) {
        this.setUserInfo()
      }
    },
    userGuid () {
      if (this.userGuid) {
        this.setUserInfo()
        this.getRoleEnvironments()
        this.getCompany()
      }
    },
    companyGuid () {
      if (this.companyGuid) {
        this.getCompany()
        this.getRoleEnvironments()
      }
    }
  },
  created () {
    this.setUserInfo()
    if (this.companyGuid) {
      this.getCompany()
      this.getRoleEnvironments()
    }
  },
  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType', 'resetAppTimeout']),
    getCompany () {
      this.$apollo.query({
        query: company,
        fetchPolicy: 'no-cache',
        variables: {
          companyGuid: this.companyGuid
        }
      }).then(({ data: { company } }) => {
        this.companyName = company.name
        if (company) {
          this.orgs = company.orgs
          this.roleTypes = company.roleTypes.filter(role => role !== 'Support Admin')

          if (!this.edit) {
            const localTimeZone = TIMEZONES.find(tz => tz.value === Intl.DateTimeFormat().resolvedOptions().timeZone)
            this.timeZone = localTimeZone == null ? this.timeZoneOptions[0] : localTimeZone.label // if local time is not in list, default to UTC
            this.defaultTimeZone = localTimeZone == null ? this.timeZoneOptions[0] : localTimeZone.label // if local time is not in list, default to UTC

            if (this.roleTypes.length > 0) {
              this.roleType = this.roleTypes[0]
            }
          }
        }
      })
    },
    getRoleEnvironments () {
      this.$apollo.query({
        query: rolesPermissions,
        variables: {
          companyGuid: this.companyGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        const { nodes } = data.roles
        const envMap = {}
        nodes.map(node => {
          const flatPermissions = JSON.parse(node.flatPermissions)
          if (flatPermissions.permission_setting_staging_enabled) {
            if (flatPermissions.permission_setting_production_enabled) {
              envMap[node.name] = ['Production', 'Staging']
            } else {
              envMap[node.name] = ['Staging']
            }
          } else if (flatPermissions.permission_setting_production_enabled) {
            envMap[node.name] = ['Production']
          }
        })
        this.roleEnvironments = envMap
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err.message}` })
      })
    },
    setUserInfo () {
      if (!this.user) {
        return
      }
      this.firstName = this.user.firstName
      this.lastName = this.user.lastName
      this.timeZone = this.user.timezone
      this.roleType = this.user.roleType
      this.email = this.user.email
      this.selectedOrgs = this.user.selectedOrgs || []

      this.dbFirstName = this.user.firstName
      this.dbLastName = this.user.lastName
      this.dbTimeZone = this.user.timezone
      this.dbEmail = this.user.email
      this.dbRoleType = this.user.roleType
      this.dbOrgs = this.selectedOrgs
    },
    handleSearchInput (value, type) {
      switch (type) {
        case 'timezone':
          this.validTimeZone = this.timeZoneOptions.includes(value)
          break
        case 'role':
          this.validRoleType = this.roleTypes.includes(value)
          break
      }
    },
    async getExistingUser () {
      if (this.edit) {
        return
      }
      const { data } = await this.$apollo.query({
        query: user,
        fetchPolicy: 'no-cache',
        variables: {
          email: this.email
        }
      })
      if (data.user) {
        this.emailErrors = ['Email already in use']
        this.emailAlreadyInUse = true
      } else {
        this.emailErrors = []
        this.emailAlreadyInUse = false
      }
    },
    resendInvite () {
      this.loading = true
      const { firstName, lastName, email, timeZone, stagingEnabled, productionEnabled, roleType } = this.user
      this.$apollo.mutate({
        mutation: inviteUser,
        variables: {
          orgGuids: this.selectedOrgGuids,
          firstName,
          lastName,
          email,
          timeZone,
          stagingEnabled,
          productionEnabled,
          roleType,
          companyGuid: this.companyGuid,
          newInvite: false
        }
      }).then(({ data }) => {
        this.loading = false
        if (data.inviteUser.error) {
          this.setErrorToastType({ description: data.inviteUser.error || 'There was an error with the request' })
          this.resendInviteHadErrors = true
        } else {
          this.setSuccessToast({ description: 'Invitation sent' })
        }
        this.resetAppTimeout()
      }).catch(err => {
        this.loading = false
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
        this.resendInviteHadErrors = true
      })
    },
    handleSave () {
      this.saveBtnLoading = true
      const { firstName, lastName, timeZone, selectedOrgGuids, roleType } = this.currentValues
      this.$apollo.mutate({
        mutation: editUserMutation,
        variables: {
          guid: this.userGuid,
          orgGuids: selectedOrgGuids,
          firstName,
          lastName,
          timeZone,
          roleType
        }
      }).then(({ data }) => {
        if (data.editUser.response) {
          const reload = snEvaultService.checkForSelfReload([this.userGuid], this.loggedInUser)
          this.setSuccessToast({ description: `Updated user successfully${reload ? '. Applying...' : ''}` })
          this.$emit('save')
          if (!reload) {
            this.saveBtnLoading = false
          }
        } else {
          this.setErrorToastType({ description: data.editUser.error })
          this.saveBtnLoading = false
        }
      }).catch(err => {
        this.setErrorToastType({ description: err })
        this.saveBtnLoading = false
      })
    },
    forcePasswordReset () {
      this.passwordResetLoading = true
      this.$apollo
        .query({
          query: updateUsersForcePasswordReset,
          fetchPolicy: 'no-cache',
          variables: { guids: [this.userGuid] }
        })
        .then(({ data }) => {
          if (data.updateUsersForcePasswordReset?.response) {
            this.setSuccessToast({ description: 'Password reset has been initiated.' })
            this.showPasswordResetModal = false
          } else if (data.updateUsersForcePasswordReset?.error) {
            this.setErrorToastType({ description: data.updateUsersForcePasswordReset?.erro })
          }
        }).catch((error) => {
          this.setErrorToastType({ description: error.message })
        }).finally(() => this.passwordResetLoading = false)
    },
    handleClick () {
      alert('clicked')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.contact-label {
  padding-bottom: 8px;
}

.user-row {
  margin: 32px 0;

  &:first-child {
    margin: 10px 0 32px 0;
  }

  &:last-child {
    margin: 32px 0 10px 0;
  }

  &:nth-child(-n + 2){
    margin: 32px 0px;
  }

  &:last-child {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 20px;
    margin-top: 24px;
  }

  .user-column:first-child {
    padding-right: 32px;
  }

  .user-column:only-child {
    padding-left: 0;
  }
}

::v-deep .orgs .v-select__selections {
  padding: 8px 0 !important;
}

::v-deep .v-select__selections .v-select__selection--comma {
  padding-right: 5px !important;
}

@media (max-width: 959px) {
  .user-row {
    margin: 0;

    &:nth-child(-n + 2){
      margin: 0;
    }

    &:first-child {
      margin-top: 32px;
    }

    &:last-child {
      margin-top: 0;
    }

    .user-column {
      margin-bottom: 32px;
    }

    .user-column:first-child {
      padding-right: 0;
    }

    .user-column:last-child {
      padding-left: 0;
    }

    .user-column:only-child {
      padding-left: 0;
    }
  }
}
</style>
